import React, { useEffect } from 'react';
import { DateRangePickerProps } from '@wojtekmaj/react-daterange-picker';
import { DatePickerProps } from 'react-date-picker/dist/entry.nostyle';
import { Value } from '@wojtekmaj/react-daterange-picker/dist/cjs/shared/types';
import styled from 'styled-components';
import moment from 'moment';

import { CustomCalendar } from '../../CustomCalendar/CustomCalendar';
import { colorStack } from '../../../../styleHelpers/colors';
import { TextComponent } from '../../Inputs/TextComponent';
import { EDropDownType } from '../../DropDown/DropDown';
import { IValue } from '../../../../entities/IPickers';
import { SelectInput } from '../../SelectInput/SelectInput';
import { useIntlMessage } from '../../IntlMessage';

const Wrapper = styled.div`
    width: 576px;
    padding: 1rem;
    border-radius: 8px;
    background: ${colorStack.white};
    display: flex;
`;

const CalendarBlend = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 999999;
    background: transparent;
`;

const Left = styled.div`
    width: 346px;
    position: relative;
    .react-calendar {
        padding: 0;
        .custom-calendar-wrapper > div {
            height: 100%;
        }
    }
`;
const Right = styled.div`
    width: 220px;
    padding-left: .5rem;
    margin-left: .5rem;
    border-left: 2px solid ${colorStack.ligthGrey};
`;

type IProps = DatePickerProps & DateRangePickerProps & {
    onChange(value: Value);
};

const DateRangeFilter: React.FC<IProps> = props => {
    const { intlFormatMessage } = useIntlMessage();
    const [fromValue, setFromValue] = React.useState<string>('');
    const [untilValue, setUntilValue] = React.useState<string>('');
    const [dropDownValue, setDropDownValue] = React.useState<IValue[]>([{ key: 'custom', text: intlFormatMessage({id: 'global.calendar.dateRange.custom'}), data: '' }]);
    const regex = /^(?:\d{1,2}|(?:\d{2}\/)|(?:\d{2}\/\d{1,2})|(?:\d{2}\/\d{2}\/?)|(?:\d{2}\/\d{2}\/\d{1,4}))$/;
    const [fromError, setFromError] = React.useState<boolean>(false);
    const [untilError, setUntilError] = React.useState<boolean>(false);

    const normalizeDateToStartOfDay = (date: Date | null): Date | null => {
        if (!date) return null;
        return moment(date).startOf('day').toDate();
    };

    const onChangeHandler = (value: Value) => {
        const normalizedFromDate = value[0] ? normalizeDateToStartOfDay(value[0]) : null;
        const normalizedToDate = value[1] ? normalizeDateToStartOfDay(value[1]) : null;

        setFromValue(normalizedFromDate ? moment(normalizedFromDate).format('DD/MM/YYYY') : '');
        setUntilValue(normalizedToDate ? moment(normalizedToDate).format('DD/MM/YYYY') : '');
        props.onChange([normalizedFromDate, normalizedToDate]);
    };

    const validateDate = (input: string): boolean => {
        const [day, month, year] = input.split('/').map(Number);

        if (!day || !month || !year) return false;
        if (month < 1 || month > 12) return false;
        if (day < 1) return false;

        const daysInMonth = new Date(year, month, 0).getDate();
        return day <= daysInMonth;
    };

    const handleFromChange = (value: string) => {
        if (value.length < 10) {
            if (!regex.test(value)) {
                setFromValue(value.slice(0, -1));
            } else {
                setFromValue(value);
            }
        } else if (value.length === 10) {
            if (regex.test(value) && validateDate(value)) {
                setFromValue(value);
                setFromError(false);

                if (untilValue && moment(value, 'DD/MM/YYYY').isAfter(moment(untilValue, 'DD/MM/YYYY'))) {
                    setUntilValue('');
                    setUntilError(false);
                    // tslint:disable-next-line:no-null-keyword
                    props.onChange([moment(value, 'DD/MM/YYYY').startOf('day').toDate(), null]);
                } else {
                    // tslint:disable-next-line:no-null-keyword
                    props.onChange([moment(value, 'DD/MM/YYYY').startOf('day').toDate(), untilValue ? moment(untilValue, 'DD/MM/YYYY').endOf('day').toDate() : null]);
                }
            } else {
                setFromValue('');
                setFromError(false);
                // tslint:disable-next-line:no-null-keyword
                props.onChange([null, untilValue ? moment(untilValue, 'DD/MM/YYYY').endOf('day').toDate() : null]);
            }
        } else {
            setFromValue(value.slice(0, 10));
        }
    };

    const handleUntilChange = (value: string) => {
        if (value.length < 10) {
            if (!regex.test(value)) {
                setUntilValue(value.slice(0, -1));
            } else {
                setUntilValue(value);
            }
        } else if (value.length === 10) {
            if (regex.test(value) && validateDate(value)) {
                setUntilValue(value);
                setUntilError(false);
                // tslint:disable-next-line:no-null-keyword
                props.onChange([moment(fromValue, 'DD/MM/YYYY').startOf('day').toDate(), value ? moment(value, 'DD/MM/YYYY').endOf('day').toDate() : null]);
            } else {
                setUntilValue('');
                setUntilError(false);
                // tslint:disable-next-line:no-null-keyword
                props.onChange([fromValue ? moment(fromValue, 'DD/MM/YYYY').startOf('day').toDate() : null, null]);
            }
        } else {
            setUntilValue(value.slice(0, 10));
        }
    };

    useEffect(() => {
        let startDate: Date | null;
        let endDate: Date | null;

        switch (dropDownValue[0].key) {
            case 'incoming':
                startDate = moment().startOf('day').toDate();
                // tslint:disable-next-line:no-null-keyword
                endDate = null;
                break;
            case 'past':
                // tslint:disable-next-line:no-null-keyword
                startDate = null;
                endDate = moment().endOf('day').toDate();
                break;
            case 'next7':
                startDate = moment().startOf('day').toDate();
                endDate = moment().add(7, 'days').endOf('day').toDate();
                break;
            case 'next45':
                startDate = moment().startOf('day').toDate();
                endDate = moment().add(45, 'days').endOf('day').toDate();
                break;
            case 'custom':
                // tslint:disable-next-line:no-null-keyword
                startDate = null;
                // tslint:disable-next-line:no-null-keyword
                endDate = null;
                break;
            default:
                startDate = moment('1970-01-01').startOf('day').toDate();
                endDate = moment().endOf('day').toDate();
        }

        onChangeHandler([startDate, endDate]);
    }, [dropDownValue]);

    return (
        <Wrapper>
            <Left>
                {dropDownValue[0].key !== 'custom' && <CalendarBlend />}
                <CustomCalendar
                    rangePicker
                    onChange={dropDownValue[0].key === 'custom' ? onChangeHandler : undefined}
                    value={props.value}
                />
            </Left>
            <Right>
                <SelectInput
                    inputLabel={intlFormatMessage({id: 'global.calendar.dateRange.title'})}
                    type={EDropDownType.DEFAULT}
                    value={dropDownValue}
                    onChange={item => setDropDownValue([item])}
                    options={[
                        { key: 'incoming', text: intlFormatMessage({id: 'global.calendar.dateRange.incoming'}), data: '' },
                        { key: 'past', text: intlFormatMessage({id: 'global.calendar.dateRange.past'}), data: '' },
                        { key: 'next7', text: intlFormatMessage({id: 'global.calendar.dateRange.next7'}), data: '' },
                        { key: 'next45', text: intlFormatMessage({id: 'global.calendar.dateRange.next45'}), data: '' },
                        { key: 'custom', text: intlFormatMessage({id: 'global.calendar.dateRange.custom'}), data: '' }
                    ]}
                />
                <br />
                <TextComponent
                    error={fromError}
                    placeholder="DD/MM/YYYY"
                    label={intlFormatMessage({id: 'global.calendar.dateRange.from'})}
                    value={fromValue}
                    onChange={handleFromChange}
                    disabled={dropDownValue[0].key !== 'custom'}
                />
                <br />
                <TextComponent
                    error={untilError}
                    placeholder="DD/MM/YYYY"
                    label={intlFormatMessage({id: 'global.calendar.dateRange.until'})}
                    value={untilValue}
                    onChange={handleUntilChange}
                    disabled={dropDownValue[0].key !== 'custom' || (dropDownValue[0].key === 'custom' && fromValue?.length !== 10)}
                />
            </Right>
        </Wrapper>
    );
};

export default DateRangeFilter;